import React, { useEffect, useState } from "react";
import Message from "../../../components/message";
import { MessageSections } from "../../../components/message/message";
import {
    BankCardsContainer,
    CreditCardContainer,
    CreditCardOverlay,
    CreditCardRemoveIconContainer,
    HoveredCreditCardContainer,
    NoPaymentMethodContainer,
    SelectedCardContainer,
    TitleContainer
} from "./styled";
import BarekamText from "../../../components/text";
import Cards from "react-credit-cards-2";
import BarekamButton, { SimpleButtons } from "../../../components/button";
import { OrderIcons, UserIcons } from "../../../components/icon/variants";
import { BarekamIcon } from "../../../components/icon/barekam-icon";
import { BindingModel } from "../../../../redux/reducers/balance-reducers";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { getBindings } from "../../../../redux/slices/balance-slice";
import paymentService from "../../../../services/payment-service";
import { toast } from "react-toastify";
import { getMessage } from "../../../shared/helper-functions";

const BankCards = ({ removable, bindings, onCardSelect, addNewPaymentMethod, onAddNewPaymentMethod }: BankCardProps) => {
    const [selectedCardIndex, setSelectedCardIndex] = useState<number | null>(null);
    const { matchesPhone } = useAppSelector(state => state.deviceReducer);
    const { messageCache, languageId } = useAppSelector(state => state.messagesReducer);

    const dispatch = useAppDispatch();


    useEffect(() => {
        if (!selectedCardIndex && !removable) {
            setSelectedCardIndex(0)
        }
    }, [selectedCardIndex, removable])

    function selectCard(index: number | null) {
        setSelectedCardIndex(index);
        onCardSelect(index);
    }

    async function deactivateCard(cardHolderId: string) {
        await paymentService.deactivateBinding(cardHolderId)
            .then(payload => {
                if (payload && payload.data && payload.data.ResponseCode == '00') {
                    toast['success'](
                        getMessage('card_deactivated', MessageSections.MAIN, messageCache, languageId), {
                        position: 'top-right',
                        autoClose: 10000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    });
                    dispatch(getBindings())
                } else {
                    toast['error'](
                        getMessage('failed_to_deactivate', MessageSections.MAIN, messageCache, languageId), {
                        position: 'top-right',
                        autoClose: 10000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    });
                }
            });
    }

    const BarekamCard = ({ cardInfo, index }: { cardInfo: BindingModel, index: number }) => {
        const card = <Cards
            number={cardInfo.CardPan.slice(0, 4) + "********" + cardInfo.CardPan.slice(-4)}
            expiry={cardInfo.ExpDate}
            cvc={''}
            name={'**** ****'}
            focused={'number'}
            locale={{ valid: '' }}
            preview={true}
        />;

        if (removable) {
            if (selectedCardIndex === index) {
                return (
                    <HoveredCreditCardContainer
                        onMouseLeave={() => selectCard(null)}>
                        <CreditCardRemoveIconContainer onClick={() => deactivateCard(cardInfo.CardHolderID)}>
                            <BarekamIcon variant={OrderIcons.removeProduct} />
                        </CreditCardRemoveIconContainer>

                        <CreditCardOverlay />

                        {card}
                    </HoveredCreditCardContainer>
                )
            } else {
                return (
                    <CreditCardContainer
                        onClick={() => selectCard(index)}
                        onMouseEnter={() => selectCard(index)}
                        onMouseLeave={() => selectCard(null)}>
                        {card}
                    </CreditCardContainer>
                )
            }
        } else {
            if (selectedCardIndex === index) {
                return (
                    <SelectedCardContainer>
                        {card}
                    </SelectedCardContainer>
                )
            } else {
                return (
                    <CreditCardContainer
                        onClick={() => selectCard(index)}>
                        {card}
                    </CreditCardContainer>
                )
            }
        }
    }

    return (
        <BankCardsContainer>
            <TitleContainer>
                <BarekamText variant={"l"} bold>
                    <Message messageKey={'payment_methods'} sectionId={MessageSections.MAIN} />
                </BarekamText>

                {addNewPaymentMethod && (
                    <BarekamButton type={SimpleButtons.LINK}
                        onClick={() => onAddNewPaymentMethod ? onAddNewPaymentMethod() : undefined}>
                        <BarekamText variant={'m'}>
                            <Message messageKey={'add_new_payment_method'}
                                sectionId={MessageSections.MAIN} />
                        </BarekamText>
                    </BarekamButton>
                )}
            </TitleContainer>

            {bindings && bindings.length
                ? bindings.map((cardInfo: BindingModel, index: number) =>
                    <BarekamCard cardInfo={cardInfo} key={cardInfo.CardPan} index={index} />
                )
                : <NoPaymentMethodContainer>
                    <BarekamIcon variant={UserIcons.noPaymentMethod}
                        size={{
                            width: matchesPhone ? '15rem' : '27rem',
                            height: matchesPhone ? '5rem' : '9rem'
                        }} />

                    <BarekamText variant={'m'} opaque bold>
                        <Message messageKey={'no_payment_method_yet'}
                            sectionId={MessageSections.MAIN} />
                    </BarekamText>
                </NoPaymentMethodContainer>
            }
        </BankCardsContainer>
    )
}

interface BankCardProps {
    removable: boolean,
    bindings: BindingModel[],
    onCardSelect: (cardIndex: number | null) => void;
    selectedCardIndex?: number | null,
    addNewPaymentMethod?: boolean,
    onAddNewPaymentMethod?: () => void;
}

export default BankCards;
